
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';

import SubscribeDialog from './UI/SubscribeDialog';
import { HodinkeeSearchModal } from './Search/HodinkeeSearchModal';
import { searchContent, searchStories } from '@/services/searchService';
import Header from './layout/Header';
import Footer from './UI/Footer';
import CookieConsent from './UI/CookieConsent';
import { Content } from '@/types/content';
import { Story } from '@/utils/storiesData';

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
  transparentHeader?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  className,
  transparentHeader = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [contentResults, setContentResults] = useState<Content[]>([]);
  const [storyResults, setStoryResults] = useState<Story[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { resolvedTheme } = useTheme();
  const [isSubscribeDialogOpen, setIsSubscribeDialogOpen] = useState(false);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [location.pathname]);

  const handleSearchOpen = () => {
    navigate('/search');
    setIsSearchOpen(false);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setContentResults([]);
    setStoryResults([]);
  };

  const handleSubscribeOpen = () => {
    setIsSubscribeDialogOpen(true);
  };

  const handleSubscribeClose = () => {
    setIsSubscribeDialogOpen(false);
  };

  function handleSearch(query: string): Promise<void> {
    if (!query.trim()) {
      setContentResults([]);
      setStoryResults([]);
      return Promise.resolve();
    }
    
    setIsSearching(true);
    
    return Promise.all([searchContent(query), searchStories(query)])
      .then(([videoResults, storyResults]) => {
        setContentResults(videoResults);
        setStoryResults(storyResults);
      })
      .catch(error => {
        console.error('Search error:', error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }

  function handleTagClick(tag: string): void {
    setIsSearchOpen(false);
    navigate(`/search?tag=${encodeURIComponent(tag)}`);
  }

  return (
    <div className={`min-h-screen ${resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background'}`}>
      <Header />
      
      <main className={cn('pt-16 md:pt-[72px]', className)}>
        {children}
      </main>
      
      <Footer />
      
      
      
      <HodinkeeSearchModal 
        isOpen={isSearchOpen} 
        onClose={handleSearchClose} 
        onSearch={handleSearch} 
        contentResults={contentResults} 
        storyResults={storyResults} 
        isSearching={isSearching} 
        onTagClick={handleTagClick} 
      />

      <SubscribeDialog 
        isOpen={isSubscribeDialogOpen} 
        onClose={handleSubscribeClose} 
      />

      <CookieConsent />
    </div>
  );
};

export default Layout;
